
import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { 
  BookOpen, 
  Shield, 
  Code, 
  Database, 
  Settings, 
  Layers,
  Users,
  FileText,
  Calendar,
  DollarSign,
  Award,
  AlertTriangle,
  CheckCircle,
  BarChart2
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";

const Documentation = () => {
  return (
    <div className="container py-8 max-w-5xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h1 className="text-3xl font-bold tracking-tight mb-2">
          NALC System Documentation
        </h1>
        <p className="text-muted-foreground mb-8">
          Comprehensive guide to the Noor Ali Learning Center Student Program & Fee Tracker
        </p>

        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2 md:grid-cols-5 h-auto">
            <TabsTrigger value="overview" className="data-[state=active]:bg-nalc-teal/10">
              <BookOpen className="h-4 w-4 mr-2" /> Overview
            </TabsTrigger>
            <TabsTrigger value="features" className="data-[state=active]:bg-nalc-teal/10">
              <CheckCircle className="h-4 w-4 mr-2" /> Features
            </TabsTrigger>
            <TabsTrigger value="architecture" className="data-[state=active]:bg-nalc-teal/10">
              <Layers className="h-4 w-4 mr-2" /> Architecture
            </TabsTrigger>
            <TabsTrigger value="security" className="data-[state=active]:bg-nalc-teal/10">
              <Shield className="h-4 w-4 mr-2" /> Security
            </TabsTrigger>
            <TabsTrigger value="faq" className="data-[state=active]:bg-nalc-teal/10">
              <FileText className="h-4 w-4 mr-2" /> FAQ
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <Card>
              <CardHeader className="bg-nalc-teal/5">
                <CardTitle className="flex items-center">
                  <BookOpen className="h-5 w-5 mr-2 text-nalc-teal" /> 
                  About NALC System
                </CardTitle>
                <CardDescription>
                  A comprehensive management system for Noor Ali Learning Center
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  <p>
                    The <strong>NALC Student Program & Fee Tracker</strong> is a comprehensive educational management system designed specifically for Noor Ali Learning Center. This application streamlines administrative tasks, enhances communication between administrators, teachers, and students, and provides robust tracking for academic progress and financial management.
                  </p>
                  <p>
                    Developed by <strong>Shukrullah Samadi</strong> (Mr Designer and Developer), the system combines modern web technologies with user-friendly interfaces to create an efficient platform for managing educational operations.
                  </p>
                  <div className="mt-6">
                    <h3 className="text-lg font-semibold mb-2">Key Benefits</h3>
                    <ul className="list-disc pl-5 space-y-2">
                      <li>Centralized management of students, teachers, and administrative staff</li>
                      <li>Comprehensive tracking of enrollment, attendance, and academic progress</li>
                      <li>Streamlined fee collection and financial reporting</li>
                      <li>Certificate generation and management</li>
                      <li>Detailed analytics and reporting capabilities</li>
                      <li>Offline functionality for use in areas with unreliable internet</li>
                      <li>Progressive Web App (PWA) capabilities for installation on various devices</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Features Tab */}
          <TabsContent value="features" className="space-y-6">
            <Card>
              <CardHeader className="bg-nalc-teal/5">
                <CardTitle className="flex items-center">
                  <CheckCircle className="h-5 w-5 mr-2 text-nalc-teal" /> 
                  Core Features
                </CardTitle>
                <CardDescription>
                  Detailed explanation of system modules and capabilities
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="student-management">
                    <AccordionTrigger className="hover:bg-muted/50 px-3 rounded-md">
                      <div className="flex items-center">
                        <Users className="h-5 w-5 mr-2 text-nalc-teal" />
                        Student Management
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-3 pb-4">
                      <ul className="list-disc pl-5 space-y-2">
                        <li>Comprehensive student profiles with personal and academic information</li>
                        <li>Enrollment tracking for various programs and courses</li>
                        <li>Document management for student records</li>
                        <li>Academic progress monitoring through exam results and assessments</li>
                        <li>Custom fields for institution-specific data</li>
                        <li>Quick search and advanced filtering options</li>
                        <li>Student payment tracking and history</li>
                      </ul>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="teacher-management">
                    <AccordionTrigger className="hover:bg-muted/50 px-3 rounded-md">
                      <div className="flex items-center">
                        <Users className="h-5 w-5 mr-2 text-blue-500" />
                        Teacher Management
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-3 pb-4">
                      <ul className="list-disc pl-5 space-y-2">
                        <li>Detailed teacher profiles with qualifications and contact information</li>
                        <li>Subject and department assignment</li>
                        <li>Timetable management and schedule optimization</li>
                        <li>Attendance tracking for teaching staff</li>
                        <li>Salary management and payment processing</li>
                        <li>Performance evaluation tools</li>
                        <li>Document storage for certificates and credentials</li>
                      </ul>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="admin-management">
                    <AccordionTrigger className="hover:bg-muted/50 px-3 rounded-md">
                      <div className="flex items-center">
                        <Settings className="h-5 w-5 mr-2 text-gray-500" />
                        Administrative Tools
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-3 pb-4">
                      <ul className="list-disc pl-5 space-y-2">
                        <li>Admin user management with role-based access control</li>
                        <li>System settings and configuration options</li>
                        <li>Action history tracking for accountability</li>
                        <li>Backup and restore functionality for data protection</li>
                        <li>Password protection for sensitive features</li>
                        <li>Notification management and delivery preferences</li>
                      </ul>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="timetable">
                    <AccordionTrigger className="hover:bg-muted/50 px-3 rounded-md">
                      <div className="flex items-center">
                        <Calendar className="h-5 w-5 mr-2 text-indigo-500" />
                        Timetable & Scheduling
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-3 pb-4">
                      <ul className="list-disc pl-5 space-y-2">
                        <li>Interactive timetable interface for class scheduling</li>
                        <li>Teacher availability management</li>
                        <li>Classroom allocation and optimization</li>
                        <li>Schedule conflict detection and resolution</li>
                        <li>Class alerts and notifications</li>
                        <li>Attendance management for classes</li>
                        <li>Substitution management for absent teachers</li>
                        <li>Printable timetable reports</li>
                      </ul>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="financial">
                    <AccordionTrigger className="hover:bg-muted/50 px-3 rounded-md">
                      <div className="flex items-center">
                        <DollarSign className="h-5 w-5 mr-2 text-green-600" />
                        Financial Management
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-3 pb-4">
                      <ul className="list-disc pl-5 space-y-2">
                        <li>Fee collection and payment tracking</li>
                        <li>Multiple payment methods support</li>
                        <li>Fee structure management by program</li>
                        <li>Revenue reporting and analysis</li>
                        <li>Teacher salary processing</li>
                        <li>Payment history and tracking</li>
                        <li>Financial dashboard with key metrics</li>
                        <li>Exportable financial reports</li>
                      </ul>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="certification">
                    <AccordionTrigger className="hover:bg-muted/50 px-3 rounded-md">
                      <div className="flex items-center">
                        <Award className="h-5 w-5 mr-2 text-amber-500" />
                        Certification System
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-3 pb-4">
                      <ul className="list-disc pl-5 space-y-2">
                        <li>Certificate template management</li>
                        <li>Custom certificate design and mapping</li>
                        <li>Automated certificate generation</li>
                        <li>Certificate verification system</li>
                        <li>Certificate history tracking</li>
                        <li>PDF export and printing capabilities</li>
                        <li>Program-specific certification requirements</li>
                      </ul>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="reporting">
                    <AccordionTrigger className="hover:bg-muted/50 px-3 rounded-md">
                      <div className="flex items-center">
                        <BarChart2 className="h-5 w-5 mr-2 text-purple-500" />
                        Analytics & Reporting
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-3 pb-4">
                      <ul className="list-disc pl-5 space-y-2">
                        <li>Dashboard with key performance indicators</li>
                        <li>Enrollment trend analysis</li>
                        <li>Program distribution charts</li>
                        <li>Revenue analytics and projections</li>
                        <li>Attendance pattern reports</li>
                        <li>Exam performance statistics</li>
                        <li>Customizable report generation</li>
                        <li>Export functionality to common formats</li>
                      </ul>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="pwa-features">
                    <AccordionTrigger className="hover:bg-muted/50 px-3 rounded-md">
                      <div className="flex items-center">
                        <Code className="h-5 w-5 mr-2 text-nalc-teal" />
                        PWA Capabilities
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-3 pb-4">
                      <ul className="list-disc pl-5 space-y-2">
                        <li>Installable on devices (desktop and mobile)</li>
                        <li>Offline functionality for core features</li>
                        <li>Data synchronization when back online</li>
                        <li>Push notifications for important events</li>
                        <li>Reduced network dependency</li>
                        <li>Application updates management</li>
                      </ul>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Architecture Tab */}
          <TabsContent value="architecture" className="space-y-6">
            <Card>
              <CardHeader className="bg-nalc-teal/5">
                <CardTitle className="flex items-center">
                  <Layers className="h-5 w-5 mr-2 text-nalc-teal" /> 
                  System Architecture
                </CardTitle>
                <CardDescription>
                  Technical foundation and implementation details
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6 space-y-6">
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Technology Stack</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="border rounded-md p-4">
                      <h4 className="font-medium text-nalc-teal flex items-center mb-2">
                        <Code className="h-4 w-4 mr-2" /> Frontend
                      </h4>
                      <ul className="list-disc pl-5 space-y-1">
                        <li>React (v18) - UI library</li>
                        <li>TypeScript - Type-safe JavaScript</li>
                        <li>Vite - Fast build tooling</li>
                        <li>React Router (v6) - Navigation</li>
                        <li>TailwindCSS - Styling framework</li>
                        <li>Shadcn UI - Component library</li>
                        <li>Framer Motion - Animations</li>
                        <li>React Query - Data fetching</li>
                        <li>Recharts - Visualization</li>
                        <li>Lucide React - Icon system</li>
                      </ul>
                    </div>

                    <div className="border rounded-md p-4">
                      <h4 className="font-medium text-nalc-teal flex items-center mb-2">
                        <Database className="h-4 w-4 mr-2" /> Storage & State
                      </h4>
                      <ul className="list-disc pl-5 space-y-1">
                        <li>IndexedDB - Client-side storage</li>
                        <li>Zustand - State management</li>
                        <li>React Context API - Context providers</li>
                        <li>File System API - Document management</li>
                        <li>LocalStorage - Preferences & settings</li>
                        <li>SQLite (Capacitor) - Mobile storage</li>
                      </ul>
                    </div>
                  </div>

                  <div className="mt-6 space-y-4">
                    <h3 className="text-lg font-semibold">Application Structure</h3>
                    <p>The application follows a modular architecture to maintain separation of concerns and support future extensibility:</p>
                    
                    <div className="border rounded-md p-4 space-y-2">
                      <h4 className="font-medium">Key Directories:</h4>
                      <ul className="list-disc pl-5 space-y-1">
                        <li><code>/components</code> - Reusable UI building blocks</li>
                        <li><code>/contexts</code> - React Context providers for global state</li>
                        <li><code>/hooks</code> - Custom React hooks for shared functionality</li>
                        <li><code>/pages</code> - Top-level page components</li>
                        <li><code>/routes</code> - Routing configuration</li>
                        <li><code>/services</code> - API and business logic services</li>
                        <li><code>/utils</code> - Helper functions and utilities</li>
                        <li><code>/types</code> - TypeScript type definitions</li>
                        <li><code>/styles</code> - Global styles and animations</li>
                      </ul>
                    </div>

                    <div className="mt-4">
                      <h4 className="font-medium mb-2">Data Flow Architecture:</h4>
                      <p>The application uses a combination of patterns:</p>
                      <ul className="list-disc pl-5 space-y-1">
                        <li>Context API for global state (themes, notifications, passwords)</li>
                        <li>React Query for data fetching, caching, and synchronization</li>
                        <li>Custom hooks for encapsulating complex business logic</li>
                        <li>Service layer for database interactions</li>
                        <li>Offline-first approach with synchronization capabilities</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">PWA Implementation</h3>
                  <div className="border rounded-md p-4">
                    <p>The application is built as a Progressive Web App (PWA) with:</p>
                    <ul className="list-disc pl-5 space-y-1 mt-2">
                      <li>Service Worker for caching and offline functionality</li>
                      <li>Web App Manifest for installation capabilities</li>
                      <li>Workbox for advanced service worker features</li>
                      <li>Background sync for offline data synchronization</li>
                      <li>IndexedDB for robust client-side storage</li>
                      <li>Push notifications with customizable sounds</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Security Tab */}
          <TabsContent value="security" className="space-y-6">
            <Card>
              <CardHeader className="bg-nalc-teal/5">
                <CardTitle className="flex items-center">
                  <Shield className="h-5 w-5 mr-2 text-nalc-teal" /> 
                  Security Features
                </CardTitle>
                <CardDescription>
                  System security measures and data protection
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold mb-3">Data Protection</h3>
                    <ul className="list-disc pl-5 space-y-2">
                      <li>
                        <strong>Local Storage Encryption:</strong> Sensitive data stored in IndexedDB is encrypted using modern cryptographic standards
                      </li>
                      <li>
                        <strong>Password Protection:</strong> Administrative features can be protected with password authentication
                      </li>
                      <li>
                        <strong>Auto-locking:</strong> Configurable system to automatically lock after periods of inactivity
                      </li>
                      <li>
                        <strong>Data Backup:</strong> Regular automated backup mechanisms with encryption options
                      </li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold mb-3">Access Control</h3>
                    <ul className="list-disc pl-5 space-y-2">
                      <li>
                        <strong>Role-based Access:</strong> Different user roles (admin, teacher, etc.) with appropriate permissions
                      </li>
                      <li>
                        <strong>Action Tracking:</strong> Comprehensive logging of administrative actions for audit purposes
                      </li>
                      <li>
                        <strong>Device Management:</strong> Control which devices can access sensitive features
                      </li>
                    </ul>
                  </div>

                  <div className="border-t pt-4">
                    <h3 className="text-lg font-semibold mb-3 flex items-center">
                      <AlertTriangle className="h-5 w-5 mr-2 text-amber-500" /> Security Best Practices
                    </h3>
                    <div className="space-y-3">
                      <div className="bg-amber-50 dark:bg-amber-950/30 p-3 rounded-md border border-amber-200 dark:border-amber-900">
                        <h4 className="font-medium mb-1">Password Management</h4>
                        <ul className="list-disc pl-5 space-y-1">
                          <li>Use a strong, unique password for the application</li>
                          <li>Change the system password periodically</li>
                          <li>Never share password credentials</li>
                          <li>Enable auto-lock features when the system is unattended</li>
                        </ul>
                      </div>

                      <div className="bg-amber-50 dark:bg-amber-950/30 p-3 rounded-md border border-amber-200 dark:border-amber-900">
                        <h4 className="font-medium mb-1">Data Backup</h4>
                        <ul className="list-disc pl-5 space-y-1">
                          <li>Regularly export backups to external storage</li>
                          <li>Verify backup integrity periodically</li>
                          <li>Store backups in secure locations</li>
                          <li>Test the restoration process occasionally</li>
                        </ul>
                      </div>

                      <div className="bg-amber-50 dark:bg-amber-950/30 p-3 rounded-md border border-amber-200 dark:border-amber-900">
                        <h4 className="font-medium mb-1">Device Security</h4>
                        <ul className="list-disc pl-5 space-y-1">
                          <li>Keep the browser updated to the latest version</li>
                          <li>Use device-level security (passwords, biometrics)</li>
                          <li>Be cautious when using on public networks</li>
                          <li>Log out when accessing from shared devices</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* FAQ Tab */}
          <TabsContent value="faq" className="space-y-6">
            <Card>
              <CardHeader className="bg-nalc-teal/5">
                <CardTitle className="flex items-center">
                  <FileText className="h-5 w-5 mr-2 text-nalc-teal" /> 
                  Frequently Asked Questions
                </CardTitle>
                <CardDescription>
                  Common questions and troubleshooting tips
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="offline">
                    <AccordionTrigger>How does offline mode work?</AccordionTrigger>
                    <AccordionContent>
                      <p>The application uses service workers and IndexedDB to enable offline functionality. When online, the app syncs data automatically. While offline, you can continue working, and any changes will be synchronized when you reconnect to the internet.</p>
                      <p className="mt-2">An offline indicator appears when you lose connection, letting you know you're working in offline mode.</p>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="backup">
                    <AccordionTrigger>How do I back up my data?</AccordionTrigger>
                    <AccordionContent>
                      <p>Navigate to Settings → Backup & Restore to access backup options. You can create manual backups or configure automatic backups. Backups can be exported as files for safekeeping. To restore data, use the import function in the same section.</p>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="password">
                    <AccordionTrigger>What if I forget my system password?</AccordionTrigger>
                    <AccordionContent>
                      <p>If you forget your system password, you'll need to use the password reset option in the Settings → Password Management section. Follow the recovery steps carefully. It's recommended to keep a secure backup of your password in case you need it.</p>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="printing">
                    <AccordionTrigger>How do I print reports or certificates?</AccordionTrigger>
                    <AccordionContent>
                      <p>Most sections that display data have print functionality via a printer icon or button. The system will generate a print-friendly version of the data. For certificates, navigate to the student's profile, select the Certifications tab, and use the print option there.</p>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="notifications">
                    <AccordionTrigger>How do I manage notifications?</AccordionTrigger>
                    <AccordionContent>
                      <p>Access notification settings through the notification bell icon or in Settings → Notification Preferences. Here you can customize which events trigger notifications, configure notification sounds, and set reminder frequencies for important events.</p>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="performance">
                    <AccordionTrigger>How can I improve system performance?</AccordionTrigger>
                    <AccordionContent>
                      <p>To optimize performance:</p>
                      <ul className="list-disc pl-5 mt-2 space-y-1">
                        <li>Regularly clear cache and temporary files in your browser</li>
                        <li>Archive old student records that are no longer active</li>
                        <li>Install the app as a PWA for better performance</li>
                        <li>Use the latest version of Google Chrome or Microsoft Edge</li>
                        <li>Export and delete old data that's rarely accessed</li>
                      </ul>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="install">
                    <AccordionTrigger>How do I install the app on my device?</AccordionTrigger>
                    <AccordionContent>
                      <p>As a Progressive Web App (PWA), you can install NALC on most devices:</p>
                      <p className="mt-2"><strong>On Desktop:</strong> Look for the install icon in your browser's address bar or navigate to App Info page for installation instructions.</p>
                      <p className="mt-2"><strong>On Mobile:</strong> Use the "Add to Home Screen" option in your browser menu or follow the installation prompt that appears when you visit the application.</p>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="support">
                    <AccordionTrigger>How can I get technical support?</AccordionTrigger>
                    <AccordionContent>
                      <p>For technical assistance:</p>
                      <ol className="list-decimal pl-5 mt-2 space-y-1">
                        <li>Visit the Help Center page for troubleshooting guides</li>
                        <li>Contact the developer via the information in the About Us page</li>
                        <li>Send an email to www.mrdesigner.af@gmail.com</li>
                        <li>Use WhatsApp support at +93 784604196</li>
                      </ol>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>

                <div className="mt-8 text-center">
                  <p className="text-muted-foreground">Need more help?</p>
                  <div className="flex justify-center mt-2 space-x-4">
                    <Link to="/help-center">
                      <Badge variant="outline" className="px-4 py-2 hover:bg-primary/10 cursor-pointer">
                        Visit Help Center
                      </Badge>
                    </Link>
                    <Link to="/about-us">
                      <Badge variant="outline" className="px-4 py-2 hover:bg-primary/10 cursor-pointer">
                        Contact Support
                      </Badge>
                    </Link>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </motion.div>
    </div>
  );
};

export default Documentation;
